// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { AppBar, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { TaskForm } from '~/components/module/task';
import { BaseInputSearch, UnderDevelopment } from '~/components/common';
// Others
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { QUOTE_PERMISSIONS, TASK_PERMISSIONS } from '~/utils/constants/permissions';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './Header.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const Header = () => {
  //#region Destructuring Props
  const { canCreateQuotes } = usePermissions(QUOTE_PERMISSIONS);
  const { canCreateTasks } = usePermissions(TASK_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const navigate = useNavigate();

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowFormTask, setIsShowFormTask] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [valueSearch, setValueSearch] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleBuildQuote = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createQuotes);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createQuotes);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createQuotes);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createQuotes);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createQuotes);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createQuotes);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createQuotes);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createQuotes);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createQuotes);
        break;
    }
  };

  const handleCreateTask = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.task);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.task);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.task);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.task);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.task);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.task);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.task);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.task);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.task);
        break;
    }
    setIsShowFormTask(true);
  };

  const handleCloseFormTask = () => {
    setIsShowFormTask(false);
  };

  const handleChangeSearch = (data: string) => {
    if (!data) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.global}?searchKey=${data}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.global}?searchKey=${data}`);
        break;
    }

    setValueSearch(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <AppBar position='static' color='secondary' sx={{ boxShadow: 0 }} className={cx('container')}>
      <Stack
        direction={'row'}
        height={'100%'}
        paddingLeft={10}
        paddingRight={'33px'}
        justifyContent={'space-between'}
        alignItems={'center'}
        spacing={5}
      >
        <BaseInputSearch
          width={440}
          value={valueSearch}
          onChange={setValueSearch}
          onSearch={handleChangeSearch}
          placeholder={t('common_text_search_header')}
        />
        <Stack direction={'row'} spacing={'13px'} alignItems={'center'} justifyContent={'end'} width={500}>
          {/* <GoogleRating rate={4.8} onSeeAll={handleSeeAll} /> */}

          {canCreateTasks && (
            <Button
              type='button'
              variant='contained'
              className={cx('button')}
              onClick={handleCreateTask}
              startIcon={<img src={icons.commonIconPlus} alt={t('common_alt_img')} />}
            >
              {t('common_button_create_task')}
            </Button>
          )}

          {canCreateQuotes && (
            <Button
              type='button'
              variant='contained'
              className={cx('button')}
              onClick={handleBuildQuote}
              startIcon={<img src={icons.commonIconPlus} alt={t('common_alt_img')} />}
            >
              {t('common_button_build_quote')}
            </Button>
          )}
        </Stack>
      </Stack>

      {isShowFormTask && <TaskForm onClose={handleCloseFormTask} />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </AppBar>
  );
};

export default Header;
